import { useRef, useEffect, useState, useCallback, type RefObject, type MouseEvent as ReactMouseEvent } from "react"
import { createPortal } from "react-dom"
import { useNavigate, useLocation } from "react-router-dom"
import { CheckCheck, ExternalLink, Mail, Sparkles, X, Trash2, type LucideIcon } from "lucide-react"
import { useModalOpen } from "../lib/useModalOpen"
import { supabase } from "../lib/supabase"
import { useAuth } from "./AuthProvider"
import { getNotificationTypeStyle, formatTimeAgo } from "../lib/notifications"
import { isDemoSite } from "../lib/isDemoSite"
import { GlitchWord } from "./GlitchWord"
import { useToast } from "./Toast"
import { useGlassTokens } from "../lib/useAppBackground"

export type UserRole = "creator" | "client" | "admin" | "operator" | null

/** DB row from public.notifications */
export interface NotificationRow {
  id: string
  recipient_email: string
  type: string
  title: string
  body: string
  read: boolean
  action_url: string | null
  metadata: Record<string, unknown> | null
  created_at: string
}

/** Shape used for list item display (derived from NotificationRow) */
export interface DisplayNotification {
  id: string
  type: string
  title: string
  body: string
  timeAgo: string
  read: boolean
  icon: LucideIcon
  iconBg: string
  iconColor: string
  action_url: string | null
  metadata: Record<string, unknown> | null
}

function rowToDisplay(row: NotificationRow): DisplayNotification {
  const style = getNotificationTypeStyle(row.type)
  return {
    id: row.id,
    type: row.type,
    title: row.title,
    body: row.body,
    timeAgo: formatTimeAgo(row.created_at),
    read: row.read,
    icon: style.icon,
    iconBg: style.iconBg,
    iconColor: style.iconColor,
    action_url: row.action_url,
    metadata: row.metadata ?? null,
  }
}

interface NotificationPanelProps {
  open: boolean
  onClose: () => void
  anchorRef: RefObject<HTMLButtonElement | null>
  role: UserRole
  onUnreadCountChange?: (count: number) => void
  /** When "left", panel aligns its left edge to anchor (opens to the right). Default "right". */
  align?: "left" | "right"
  /** Called when full-screen modal opens so the parent (e.g. sidebar) can collapse. */
  onCloseSidebar?: () => void
}

/** Formats a notification type string for display: "apply_capture" → "APPLY CAPTURE" */
function formatType(type: string): string {
  return type.replace(/_/g, " ").toUpperCase()
}

function NotificationListItem({
  n,
  isRead,
  onClick,
  onMarkUnread,
  onDelete,
}: {
  n: DisplayNotification
  isRead: boolean
  onClick: () => void
  onMarkUnread?: () => void
  onDelete?: () => void
}) {
  const Icon = n.icon
  return (
    <li className="group relative">
      <button
        type="button"
        onClick={onClick}
        className="flex w-full gap-3 px-4 py-3 text-left transition-all duration-150 hover:bg-white/[0.04] focus:outline-none"
      >
        {/* Icon */}
        <span
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${n.iconBg} ${n.iconColor}`}
        >
          <Icon className="h-3.5 w-3.5" />
        </span>

        {/* Content — hierarchy: title primary, body secondary, type+time tertiary */}
        <div className="min-w-0 flex-1">
          <p className={`text-[13px] font-semibold leading-snug truncate ${isRead ? "text-white/50" : "text-white/90"}`}>
            {n.title}
          </p>
          <p
            className="mt-0.5 line-clamp-2 text-[11px] leading-relaxed text-white/40 truncate"
            title={n.body}
          >
            {n.body}
          </p>
          <div className="flex items-center justify-between gap-2 mt-1">
            <span className="text-[9px] font-mono font-bold uppercase tracking-wider text-white/25">
              {formatType(n.type)}
            </span>
            <span className="text-[9px] font-mono text-white/20 shrink-0 tabular-nums">{n.timeAgo}</span>
          </div>
        </div>

        {/* Unread indicator */}
        {!isRead && (
          <div className="flex h-8 items-center shrink-0">
            <span className="h-1.5 w-1.5 rounded-full bg-luster-400 shadow-[0_0_6px_rgba(167,139,250,0.5)]" />
          </div>
        )}
      </button>

      {/* Row actions: mark unread, delete */}
      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
        {isRead && onMarkUnread && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onMarkUnread() }}
            className="rounded-lg p-1.5 text-white/20 hover:bg-white/[0.06] hover:text-white/50"
            title="Mark unread"
            aria-label="Mark unread"
          >
            <Mail className="h-3.5 w-3.5" />
          </button>
        )}
        {onDelete && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDelete() }}
            className="rounded-lg p-1.5 text-white/20 hover:bg-red-500/10 hover:text-red-400"
            title="Delete"
            aria-label="Delete notification"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </li>
  )
}

const DESKTOP_BREAKPOINT = 1024

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(true)
  useEffect(() => {
    const m = window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`)
    setIsDesktop(m.matches)
    const onchange = () => setIsDesktop(m.matches)
    m.addEventListener("change", onchange)
    return () => m.removeEventListener("change", onchange)
  }, [])
  return isDesktop
}

export function NotificationPanel({
  open,
  onClose,
  anchorRef,
  role,
  onUnreadCountChange,
  align = "right",
  onCloseSidebar,
}: NotificationPanelProps) {
  const glass = useGlassTokens()
  const panelRef = useRef<HTMLDivElement>(null)
  const detailModalContainerRef = useRef<HTMLDivElement>(null)
  const [fullModalOpen, setFullModalOpen] = useState(false)
  const [notifications, setNotifications] = useState<DisplayNotification[]>([])
  const [loading, setLoading] = useState(true)
  const isDesktop = useIsDesktop()
  const { user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [detailModalNotification, setDetailModalNotification] = useState<DisplayNotification | null>(null)

  // On the demo site there is no Supabase Auth session, so user is null.
  // Fall back to the seeded demo admin email so the query runs and the RLS
  // anon policy (is_demo = true) allows the read.
  const DEMO_EMAIL = "admin@demo.luster.company"
  const isDemo = isDemoSite()
  // Prefer authenticated user email when present (even on demo domain),
  // otherwise fall back to seeded demo admin for anonymous visitors.
  const resolvedEmail = user?.email ?? (isDemo ? DEMO_EMAIL : null)

  const fetchNotifications = useCallback(async () => {
    if (!resolvedEmail) {
      setNotifications([])
      setLoading(false)
      return
    }
    setLoading(true)
    const { data: rows, error } = await supabase
      .from("notifications")
      .select("id, recipient_email, type, title, body, read, action_url, metadata, created_at")
      .eq("is_demo", isDemo)
      .eq("recipient_email", resolvedEmail)
      .order("created_at", { ascending: false })
      .limit(100)
    setLoading(false)
    if (error) {
      console.error("Notifications fetch error:", error)
      setNotifications([])
      return
    }
    // apply_capture is creator-only; beta_lead is legacy — hide from everyone
    const filtered = (rows as NotificationRow[]).filter(
      (r) =>
        r.type !== "beta_lead" &&
        ((r.type !== "apply_capture" && r.type !== "demo_booked" && r.type !== "lead_invited") || role === "creator")
    )
    setNotifications(filtered.map(rowToDisplay))
  }, [resolvedEmail, isDemo, role])

  useEffect(() => {
    fetchNotifications()
  }, [fetchNotifications])

  // Realtime: listen for INSERT/DELETE on own notifications only.
  // Filter by recipient_email so other users' events don't trigger a re-fetch.
  useEffect(() => {
    if (isDemo || !user?.email) return
    const channel = supabase
      .channel(`notifications:${user.email}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `recipient_email=eq.${user.email}`,
        },
        (payload) => {
          // Append the new notification without a full re-fetch
          const row = payload.new as NotificationRow
          if (row.type === "beta_lead") return
          if ((row.type === "apply_capture" || row.type === "demo_booked" || row.type === "lead_invited") && role !== "creator") return
          setNotifications((prev) => [rowToDisplay(row), ...prev])
        }
      )
      .subscribe()
    return () => {
      supabase.removeChannel(channel)
    }
  }, [isDemo, user?.email, role])

  const unreadCount = notifications.filter((n) => !n.read).length
  useEffect(() => {
    onUnreadCountChange?.(unreadCount)
  }, [unreadCount, onUnreadCountChange])

  // In demo: mutations are local-state-only so each visitor gets a fresh
  // unread state on every page load and shared demo data is never mutated.
  const markAsRead = useCallback(
    async (id: string) => {
      if (!isDemo) await supabase.from("notifications").update({ read: true }).eq("id", id)
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)))
    },
    [isDemo]
  )

  const markAsUnread = useCallback(
    async (id: string) => {
      if (!isDemo) await supabase.from("notifications").update({ read: false }).eq("id", id)
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: false } : n)))
    },
    [isDemo]
  )

  const markAllAsRead = useCallback(async () => {
    if (!isDemo) {
      await supabase.from("notifications").update({ read: true }).eq("is_demo", false).eq("recipient_email", resolvedEmail ?? "")
    }
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })))
  }, [isDemo, resolvedEmail])

  const { toast } = useToast()
  const handleDelete = useCallback(
    async (id: string) => {
      // Optimistically remove from local state immediately so UI feels instant
      setNotifications((prev) => prev.filter((n) => n.id !== id))
      setDetailModalNotification((current) => (current?.id === id ? null : current))

      const { data, error } = await supabase.from("notifications").delete().eq("id", id).select()
      if (error) {
        console.error("Notification delete error:", error)
        toast("Failed to delete — check console", "error")
        // Re-fetch so UI is consistent with DB
        fetchNotifications()
      } else if (!data || data.length === 0) {
        console.warn("Notification delete: no rows affected (RLS or already gone)", id)
        // Already removed from local state; if it comes back on next fetch it's an RLS issue
      }
      // Success: already removed from state above
    },
    [toast, fetchNotifications]
  )

  const handleDeleteAll = useCallback(async () => {
    if (!resolvedEmail) return
    const { error } = await supabase
      .from("notifications")
      .delete()
      .eq("recipient_email", resolvedEmail)
    if (error) {
      toast("Failed to delete notifications", "error")
      console.error(error)
    } else {
      setNotifications([])
      setDetailModalNotification(null)
      toast("All notifications deleted", "success")
    }
  }, [resolvedEmail, toast])

  const openFullModal = () => {
    setFullModalOpen(true)
    onClose()
    onCloseSidebar?.()
  }

  const handleNotificationClick = (id: string) => {
    const n = notifications.find((x) => x.id === id)
    if (!n) return
    markAsRead(id)
    setDetailModalNotification(n)
    onCloseSidebar?.()
  }

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e: MouseEvent) => {
      const anchor = anchorRef.current
      const panel = panelRef.current
      const detailContainer = detailModalContainerRef.current
      // Don't close when clicking inside the notification detail modal (e.g. backdrop or dialog)
      if (detailContainer?.contains(e.target as Node)) return
      if (
        panel &&
        !panel.contains(e.target as Node) &&
        anchor &&
        !anchor.contains(e.target as Node)
      ) {
        onClose()
      }
    }
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("mousedown", handleClickOutside)
    document.addEventListener("keydown", handleEscape)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      document.removeEventListener("keydown", handleEscape)
    }
  }, [open, onClose, anchorRef])

  const showFullModal = (open && !isDesktop) || fullModalOpen
  const fullModalOnClose = open && !isDesktop ? onClose : () => setFullModalOpen(false)

  // Keep mounted when detail modal (e.g. Invoice) is open so it can render and be dismissable
  if (!open && !fullModalOpen && !detailModalNotification) return null

  return (
    <>
      {/* ── Desktop dropdown panel ────────────────────────────── */}
      {open && isDesktop && createPortal(
        <>
          <div className={`fixed inset-0 z-[80] ${glass.overlay}`} onClick={onClose} aria-hidden />
          <div
            ref={panelRef}
            role="dialog"
            aria-label="Notifications"
            className={`fixed left-72 top-28 z-[81] w-full min-w-[320px] max-w-[380px] overflow-hidden rounded-2xl ${glass.modalBg} backdrop-blur-2xl border border-white/[0.08] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.7)] flex flex-col`}
          >
          {/* Header */}
          <div className="flex shrink-0 items-center justify-between border-b border-white/[0.06] px-4 py-3">
            <div className="flex flex-col gap-0.5">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-white/90">
                  Signal
                </span>
                {unreadCount > 0 && (
                  <span className="rounded-full bg-luster-400/15 border border-luster-400/20 px-2 py-0.5 text-[9px] font-mono font-bold text-luster-300 uppercase tracking-wider">
                    {unreadCount} new
                  </span>
                )}
              </div>
              <div className="font-mono text-[9px] flex items-center gap-1.5">
                <span className="text-white/15">0x0B</span>
                <span className="text-white/10">&middot;</span>
                <span className="text-luster-400/60">
                  <GlitchWord word="signal" intensity={1} colorBase="violet" />.scan()
                </span>
              </div>
            </div>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={markAllAsRead}
                className="flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-luster-300 bg-luster-400/10 border border-luster-400/20 hover:bg-luster-400/15 transition-colors whitespace-nowrap"
                title="Mark all as read"
              >
                <CheckCheck className="h-3 w-3 shrink-0" />
                Clear all
              </button>
            )}
          </div>

          {/* List */}
          <div className="min-h-0 flex-1 overflow-y-auto max-h-[min(70vh,400px)]">
            {loading ? (
              <div className="px-4 py-8 text-center text-[11px] font-mono text-white/25">
                fetching…
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <p className="text-[11px] font-mono text-white/20">// no activity</p>
                <p className="mt-1 text-[11px] text-white/25">No notifications yet.</p>
              </div>
            ) : (
              <ul className="divide-y divide-white/[0.05]">
                {notifications.map((n) => (
                  <NotificationListItem
                    key={n.id}
                    n={n}
                    isRead={n.read}
                    onClick={() => handleNotificationClick(n.id)}
                    onDelete={() => handleDelete(n.id)}
                  />
                ))}
              </ul>
            )}
          </div>

          {/* Footer */}
          <div className="flex shrink-0 items-center border-t border-white/[0.06] px-4 py-2.5">
            <button
              type="button"
              onClick={openFullModal}
              className="flex-1 rounded-xl py-1.5 text-center text-[11px] font-mono font-bold uppercase tracking-[0.1em] text-white/30 hover:bg-white/[0.04] hover:text-white/60 transition-colors"
            >
              → All Activity
            </button>
          </div>
          </div>
        </>,
        document.body
      )}

      {/* ── Full modal (mobile always, desktop on "All Activity") ── */}
      {showFullModal &&
        createPortal(
          <NotificationsFullModal
            notifications={notifications}
            onMarkAsRead={markAsRead}
            onMarkAsUnread={markAsUnread}
            onMarkAllAsRead={markAllAsRead}
            onClose={fullModalOnClose}
            onNotificationClick={handleNotificationClick}
            onDelete={handleDelete}
            onDeleteAll={handleDeleteAll}
            role={role ?? undefined}
          />,
          document.body
        )}

      {/* ── Notification detail modal ─────────────────────────── */}
      {detailModalNotification &&
        createPortal(
          <div ref={detailModalContainerRef} data-notification-detail>
            <NotificationDetailModal
              notification={detailModalNotification}
              onClose={() => setDetailModalNotification(null)}
              onGoTo={(() => {
                const n = detailModalNotification
                const reportId =
                  (n.metadata && typeof n.metadata.report_id === "string" && n.metadata.report_id) ||
                  (n.action_url && /\/admin\/reports\/([a-f0-9-]+)/i.exec(n.action_url)?.[1])
                if (reportId && (n.type === "report" || n.type === "supply")) {
                  return () => {
                    const params = new URLSearchParams(location.search)
                    params.set("activity", reportId)
                    setDetailModalNotification(null)
                    onClose()
                    navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true })
                  }
                }
                const updateId =
                  (n.metadata && typeof n.metadata.update_id === "string" && n.metadata.update_id) ||
                  (n.action_url && /[?&]update=([a-f0-9-]+)/i.exec(n.action_url)?.[1])
                if (updateId && (n.type === "incident" || n.type === "portal_update_draft")) {
                  const base = role === "creator" ? "/creator/updates" : "/updates"
                  return () => {
                    setDetailModalNotification(null)
                    onClose()
                    navigate(`${base}?update=${updateId}`)
                  }
                }
                if (n.action_url) {
                  return () => {
                    setDetailModalNotification(null)
                    onClose()
                    navigate(n.action_url!)
                  }
                }
                return undefined
              })()}
              onDelete={() => handleDelete(detailModalNotification.id)}
            />
          </div>,
          document.body
        )}
    </>
  )
}

function NotificationDetailModal({
  notification,
  onClose,
  onGoTo,
  onDelete,
}: {
  notification: DisplayNotification
  onClose: () => void
  onGoTo?: () => void
  onDelete?: () => void
}) {
  useModalOpen(true)
  const glass = useGlassTokens()
  const Icon = notification.icon
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [onClose])
  return (
    <>
      <div
        className={`fixed inset-0 z-[90] modal-backdrop ${glass.overlay}`}
        onClick={onClose}
        onPointerDown={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal
        aria-labelledby="notification-detail-title"
        className="fixed inset-0 z-[90] flex items-end justify-center md:items-center md:p-6"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose()
        }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div
          className={`w-[95%] max-h-[95dvh] flex flex-col rounded-t-3xl md:w-full md:max-w-md md:rounded-2xl ${glass.modalBg} backdrop-blur-2xl border border-white/[0.08] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.7)] overflow-hidden`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Mobile drag handle */}
          <div className="flex shrink-0 justify-center pt-2.5 pb-0 md:hidden landscape:hidden" aria-hidden>
            <div className="h-1 w-10 rounded-full bg-white/20" />
          </div>

          {/* Header — terminal trace */}
          <div className="flex shrink-0 flex-col gap-1 border-b border-white/[0.06] px-4 py-3">
            <div className="flex items-center gap-3">
              <span
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${notification.iconBg} ${notification.iconColor}`}
              >
                <Icon className="h-3.5 w-3.5" />
              </span>
              <div className="min-w-0 flex-1">
                <h2
                  id="notification-detail-title"
                  className="text-[13px] font-semibold text-white/90 truncate leading-tight"
                >
                  {notification.title}
                </h2>
                <div className="font-mono text-[9px] flex items-center gap-1.5 mt-0.5">
                  <span className="text-white/15">0x0B</span>
                  <span className="text-white/10">&middot;</span>
                  <span className="text-white/25">
                    <GlitchWord word="signal" intensity={1} colorBase="violet" />.read()
                  </span>
                  <span className="text-white/10">&middot;</span>
                  <span className="text-white/20">{formatType(notification.type)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-4 space-y-4">
            <p className="text-[12px] text-white/55 leading-relaxed whitespace-pre-wrap">
              {notification.body}
            </p>
            <div className="flex flex-wrap gap-2">
              {onGoTo && (
                <button
                  type="button"
                  onClick={onGoTo}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-teal-400/25 bg-teal-500/15 px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-teal-400 hover:bg-teal-500/25 transition-colors touch-manipulation"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Go to page
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/[0.08] bg-white/[0.04] px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-white/50 hover:bg-white/[0.08] transition-colors touch-manipulation"
              >
                Dismiss
              </button>
              {onDelete && (
                <button
                  type="button"
                  onClick={onDelete}
                  className="inline-flex items-center gap-2 rounded-lg border border-red-400/20 bg-red-500/10 px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-red-400/80 hover:bg-red-500/20 transition-colors touch-manipulation"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

/** Types that represent reports or report-linked predictions (same tab, link to report). */
const REPORT_AND_PREDICTION_TYPES = ["report", "supply"] as const
function isReportOrPrediction(n: DisplayNotification): boolean {
  if (REPORT_AND_PREDICTION_TYPES.includes(n.type as (typeof REPORT_AND_PREDICTION_TYPES)[number]))
    return true
  if (n.metadata && typeof n.metadata.report_id === "string") return true
  return false
}

/** Lead-related types: apply step 1, demo booked, lead invited. Creator-only tab. */
const LEAD_TYPES = ["apply_capture", "demo_booked", "lead_invited"] as const
function isLeadNotification(n: DisplayNotification): boolean {
  return LEAD_TYPES.includes(n.type as (typeof LEAD_TYPES)[number])
}

interface NotificationsFullModalProps {
  notifications: DisplayNotification[]
  onMarkAsRead: (id: string) => void
  onMarkAsUnread: (id: string) => void
  onMarkAllAsRead: () => void
  onClose: () => void
  onNotificationClick: (id: string) => void
  onDelete: (id: string) => void
  onDeleteAll: () => void
  /** When "creator", show the Leads tab. */
  role?: string
}

type PendingConfirm = { type: "one"; id: string } | { type: "all" } | null

function NotificationsFullModal({
  notifications,
  onMarkAsRead,
  onMarkAsUnread,
  onMarkAllAsRead,
  onClose,
  onNotificationClick,
  onDelete,
  onDeleteAll,
  role,
}: NotificationsFullModalProps) {
  useModalOpen(true)
  const glass = useGlassTokens()
  const [filter, setFilter] = useState<"all" | "unread" | "reports" | "leads">("all")
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm>(null)
  const unreadCount = notifications.filter((n) => !n.read).length
  const reportsCount = notifications.filter(isReportOrPrediction).length
  const leadsCount = notifications.filter(isLeadNotification).length
  const showLeadsTab = role === "creator"

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (pendingConfirm) { setPendingConfirm(null); return }
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [onClose, pendingConfirm])

  const filtered = notifications.filter((n) => {
    if (filter === "unread") return !n.read
    if (filter === "reports") return isReportOrPrediction(n)
    if (filter === "leads") return isLeadNotification(n)
    return true
  })

  function handleConfirm() {
    if (!pendingConfirm) return
    if (pendingConfirm.type === "one") onDelete(pendingConfirm.id)
    else onDeleteAll()
    setPendingConfirm(null)
  }

  const handleMarkAllClick = (e: ReactMouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onMarkAllAsRead()
  }

  return (
    <>
      <div
        className={`fixed inset-0 z-[85] modal-backdrop ${glass.overlay}`}
        onClick={onClose}
        onPointerDown={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal
        aria-labelledby="notifications-modal-title"
        className={`fixed bottom-0 left-1/2 -translate-x-1/2 w-[95%] h-[98dvh] z-[85] flex flex-col overflow-hidden rounded-t-3xl md:w-full md:max-w-3xl md:inset-auto md:bottom-auto md:left-1/2 md:top-1/2 md:h-[min(85vh,700px)] md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-[2rem] ${glass.modalBg} backdrop-blur-2xl border border-white/[0.08] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.7)}`}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        {/* Mobile drag handle */}
        <div className="flex shrink-0 justify-center pt-2.5 pb-0 md:hidden landscape:hidden" aria-hidden>
          <div className="h-1 w-10 rounded-full bg-white/20" />
        </div>

        {/* Header — terminal trace */}
        <div className="flex shrink-0 items-center justify-between border-b border-white/[0.06] px-5 py-3">
          <div>
            <h2
              id="notifications-modal-title"
              className="text-[11px] font-mono font-bold uppercase tracking-widest text-white/90"
            >
              Signal
            </h2>
            <div className="font-mono text-[9px] flex items-center gap-1.5 mt-0.5">
              <span className="text-white/15">0x0B</span>
              <span className="text-white/10">&middot;</span>
              <span className="text-luster-400/60">
                <GlitchWord word="signal" intensity={1} colorBase="violet" />.scan()
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={handleMarkAllClick}
                className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider text-luster-300 bg-luster-400/10 border border-luster-400/20 hover:bg-luster-400/15 transition-colors whitespace-nowrap"
                title="Mark all as read"
              >
                <CheckCheck className="h-3 h-3 shrink-0" />
                Clear all
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 text-white/30 hover:bg-white/[0.06] hover:text-white/70 transition-colors"
              aria-label="Close notifications"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Pipeline tabs + bulk actions */}
        <div className="flex shrink-0 items-center gap-1.5 border-b border-white/[0.06] px-4 py-2 overflow-x-auto">
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            {[
              { key: "all" as const, label: "All", count: notifications.length },
              { key: "unread" as const, label: "Unread", count: unreadCount, highlight: unreadCount > 0 },
              { key: "reports" as const, label: "Reports", count: reportsCount, accent: "teal" as const },
              ...(showLeadsTab ? [{ key: "leads" as const, label: "Leads", count: leadsCount }] : []),
            ].map(({ key, label, count, highlight, accent }) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-mono text-[10px] font-bold uppercase tracking-wider transition-colors border whitespace-nowrap ${
                  filter === key
                    ? accent === "teal"
                      ? "bg-teal-500/15 border-teal-400/25 text-teal-300"
                      : "bg-luster-400/20 border-luster-400/25 text-luster-300"
                    : "border-white/[0.06] bg-white/[0.02] text-white/35 hover:bg-white/[0.04] hover:text-white/50"
                }`}
              >
                {label}
                {count > 0 && (
                  <span className={`opacity-70 ${highlight ? "text-luster-300" : ""}`}>{count}</span>
                )}
              </button>
            ))}
          </div>
          {notifications.length > 0 && (
            <button
              type="button"
              onClick={() => setPendingConfirm({ type: "all" })}
              className="ml-auto shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-white/[0.06] bg-white/[0.02] text-white/25 hover:bg-red-500/10 hover:border-red-500/20 hover:text-red-400 font-mono text-[10px] font-bold uppercase tracking-wider transition-colors whitespace-nowrap"
              title="Delete all notifications"
            >
              <Trash2 className="w-3 h-3" />
              Delete all
            </button>
          )}
        </div>

        {/* Notification list */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <div className="flex-1 overflow-y-auto overscroll-contain custom-scrollbar">
              {filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-12 text-center h-full">
                  <div className="w-14 h-14 rounded-2xl bg-white/[0.04] border border-white/[0.06] flex items-center justify-center mb-4">
                    <CheckCheck className="w-6 h-6 text-white/20" />
                  </div>
                  <p className="text-[11px] font-mono text-white/20 mb-1">// no entries</p>
                  <h3 className="font-bold text-white text-sm">All caught up</h3>
                  <p className="text-[11px] text-white/30 mt-1">
                    {filter === "all"
                      ? "No notifications yet."
                      : filter === "reports"
                        ? "No report notifications."
                        : filter === "leads"
                          ? "No lead notifications yet."
                          : "No unread notifications."}
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-white/[0.05]">
                  {filtered.map((n) => (
                    <NotificationListItem
                      key={n.id}
                      n={n}
                      isRead={n.read}
                      onClick={() => onNotificationClick(n.id)}
                      onMarkUnread={() => onMarkAsUnread(n.id)}
                      onDelete={() => setPendingConfirm({ type: "one", id: n.id })}
                    />
                  ))}
                </ul>
              )}
            </div>
        </div>

      </div>

      {/* In-modal delete confirmation — portal so it doesn't affect modal stacking */}
      {pendingConfirm && createPortal(
        <>
          <div
            className={`fixed inset-0 z-[90] ${glass.overlay} backdrop-blur-sm`}
            onClick={() => setPendingConfirm(null)}
          />
          <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-[95%] z-[91] md:w-full md:max-w-sm md:bottom-auto md:top-1/2 md:-translate-y-1/2 md:left-1/2 md:-translate-x-1/2">
            <div className="bg-stone-950 border border-white/[0.08] rounded-t-2xl md:rounded-2xl p-5 space-y-4 shadow-2xl">
              <div>
                <p className="font-mono text-[10px] text-white/30 uppercase tracking-widest mb-1">
                  {pendingConfirm.type === "all" ? "// delete.all()" : "// delete.one()"}
                </p>
                <p className="font-mono text-[13px] font-bold text-white/90">
                  {pendingConfirm.type === "all"
                    ? `Delete all ${notifications.length} notification${notifications.length !== 1 ? "s" : ""}?`
                    : "Delete this notification?"}
                </p>
                <p className="font-mono text-[11px] text-white/40 mt-1">This cannot be undone.</p>
              </div>
              <div className="flex items-center gap-2 pb-safe">
                <button
                  type="button"
                  onClick={handleConfirm}
                  className="flex-1 py-2.5 rounded-xl font-mono text-[11px] font-bold uppercase tracking-wider bg-red-500/20 border border-red-500/30 text-red-400 hover:bg-red-500/30 transition-colors"
                >
                  Delete
                </button>
                <button
                  type="button"
                  onClick={() => setPendingConfirm(null)}
                  className="flex-1 py-2.5 rounded-xl font-mono text-[11px] font-bold uppercase tracking-wider border border-white/[0.08] bg-white/[0.04] text-white/50 hover:bg-white/[0.08] transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  )
}
